import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer } from 'preact-i18n';

import localUtil from '../../lib/util';
import fields from '../../lib/fields';
import FormFields from '../../components/form/fields';
import { appConfirm } from '../../lib/dialogs';
import { beginDialogAction } from '../../lib/dialogAction';

@observer
class LotTestingView extends Component {
    constructor(props) {
        super(props);
        this.state = {
            sections: fields.get('lotTesting'),
            extractResult: null,
        };
    }

    componentDidMount() {
        const { id, isNew } = this.props;
        if (id && !isNew) {
            this.loadLotTesting(id);
        }
    }

    loadLotTesting = async (id) => {
        const { lotTestingStore } = this.props.stores;
        await lotTestingStore.loadWithComments(id);
    }

    saveLotTesting = async () => {
        const { lotTestingStore, appState } = this.props.stores;
        const { isNew, callback } = this.props;

        if (isNew) {
            // Use insert for new items (POST)
            await lotTestingStore.insert(lotTestingStore.newLotTesting);
            lotTestingStore.updateKeyValue('newLotTesting', {});
        } else {
            // Use save for existing items (PATCH)
            await lotTestingStore.save(lotTestingStore.lotTesting);
        }

        if (callback) {
            callback();
        }
        appState.toggleDrawer();
    }

    addWeaponTest = () => {
        const { isNew, id, drawerLevel = 1 } = this.props;
        const { appState, lotTestingStore } = this.props.stores;
        const { drawerHeightMedium } = appState;
        const { lotTesting } = lotTestingStore;

        appState.openDrawer('addWeaponTest', {
            height: drawerHeightMedium,
            lotTestingId: isNew ? null : (id || lotTesting.id),
            isNew: true,
            isNewLotTesting: isNew,
            callback: () => {
                if (!isNew) {
                    lotTestingStore.loadWithComments(id || lotTesting.id);
                }
            },
        }, drawerLevel + 1);
    }

    editWeaponTest = (test) => {
        const { isNew, id, drawerLevel = 1 } = this.props;
        const { appState, lotTestingStore } = this.props.stores;
        const { drawerHeightMedium } = appState;
        const { lotTesting } = lotTestingStore;

        appState.openDrawer('editWeaponTest', {
            height: drawerHeightMedium,
            lotTestingId: isNew ? null : (id || lotTesting.id),
            testUuidv4: test.uuidv4,
            isNew: false,
            isNewLotTesting: isNew,
            callback: () => {
                if (!isNew) {
                    lotTestingStore.loadWithComments(id || lotTesting.id);
                }
            },
        }, drawerLevel + 1);
    }

    removeWeaponTest = async (e, testUuidv4) => {
        e.stopPropagation();
        const dialogAction = beginDialogAction(this, 'removeWeaponTest', () => [
            this.props?.id,
        ]);
        if (!dialogAction) return false;
        try {

            const { isNew, id } = this.props;
            const { lotTestingStore } = this.props.stores;

            if (((await appConfirm('Are you sure you want to remove this weapon test?')) && dialogAction.isCurrent())) {
                if (isNew) {
                    lotTestingStore.removeWeaponTestFromNew(testUuidv4);
                } else {
                    await lotTestingStore.removeWeaponTest(id, testUuidv4);
                }
            }
        } finally {
            dialogAction.finish();
        }
    }

    handleAiExtract = async () => {
        const { lotTestingStore } = this.props.stores;
        const { isNew, id } = this.props;
        const objectName = isNew ? 'newLotTesting' : 'lotTesting';
        const lot = lotTestingStore[objectName] || {};
        const lotId = isNew ? null : (id || lot.id);

        const imageKeys = (lot.images || [])
            .map(img => img.newFilename)
            .filter(Boolean)
            .slice(0, 3);

        if (imageKeys.length === 0) return;

        const result = await lotTestingStore.extractFromImages(imageKeys, lotId);
        if (!result) {
            this.setState({ extractResult: { error: true, lotCount: 0, testCount: 0 } });
            return;
        }

        const lotFields = result.lotFields || {};
        const weaponTests = result.weaponTests || [];

        let lotCount = 0;
        if (Object.keys(lotFields).length > 0) {
            lotCount = await lotTestingStore.mergeLotFields(lotFields, isNew, lotId);
        }
        if (weaponTests.length > 0) {
            await lotTestingStore.appendWeaponTests(weaponTests, lotId);
        }

        this.setState({
            extractResult: {
                lotCount,
                testCount: weaponTests.length,
            },
        });
    }

    dismissExtractResult = () => {
        this.setState({ extractResult: null });
    }

    render() {
        const { lotTestingStore, userStore } = this.props.stores;
        const { isNew, drawerLevel = 1, id } = this.props;
        const { sections } = this.state;

        const objectName = isNew ? 'newLotTesting' : 'lotTesting';
        const lotTesting = lotTestingStore[objectName] || {};
        const weaponTests = lotTesting.weaponTests || [];
        const {
            user = {}, isAdmin, isCustomerAdmin, isRangeOfficer,
        } = userStore;

        // New entries: club officers only (same gate as the backend POST).
        const canEdit = isAdmin || lotTesting.user === user.id
            || (isNew && !!(isCustomerAdmin || isRangeOfficer));

        return (
            <div class='lot-testing-view'>
                <div class='w-100 d-flex justify-content-center'>
                    <div class='w-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>

                        <div class='font-weight-lighter px-3 box-header mt-0 d-flex align-items-center'>
                            {isNew ? 'New LOT Testing Entry' : 'Edit LOT Testing Entry'}
                            {!isNew && lotTesting.isPublic && (
                                <span class='badge bg-success ms-2' style='font-size: 0.7rem;'>
                                    <i class='fa-solid fa-globe me-1' />
                                    Public
                                </span>
                            )}
                        </div>

                        <div class='w-100 d-flex flex-column justify-content-start'>
                            <div class='bg-light d-flex flex-column justify-content-start h-100 pt-3'>
                                <FormFields
                                    stores={this.props.stores}
                                    sections={sections}
                                    holdingStore={lotTestingStore}
                                    objectName={objectName}
                                    isNew={isNew}
                                    drawerLevel={drawerLevel}
                                />
                            </div>
                        </div>

                        {/* AI Extract Section */}
                        {(lotTesting.images?.length > 0) && (
                            <div class='px-3 mt-3'>
                                <div class='alert alert-info d-flex align-items-center justify-content-between mb-0'>
                                    <div class='flex-grow-1 me-3'>
                                        <div><strong><Text id='lotTesting.aiExtract.title'>AI Extract</Text></strong></div>
                                        <small class='text-body-secondary'>
                                            <Text id='lotTesting.aiExtract.description'>Extract ammo box info and chronograph readings from your uploaded images.</Text>
                                        </small>
                                    </div>
                                    <button
                                        type='button'
                                        class='btn btn-primary rounded-pill'
                                        disabled={lotTestingStore.isExtracting}
                                        onClick={this.handleAiExtract}
                                    >
                                        {lotTestingStore.isExtracting ? (
                                            <>
                                                <span class='spinner-border spinner-border-sm me-2' />
                                                <Text id='lotTesting.aiExtract.loading'>Analyzing...</Text>
                                            </>
                                        ) : (
                                            <>
                                                <i class='fa-duotone fa-robot me-2' />
                                                <Text id='lotTesting.aiExtract.button'>Extract with AI</Text>
                                            </>
                                        )}
                                    </button>
                                </div>
                                {this.state.extractResult && !this.state.extractResult.error && (
                                    <div class='alert alert-success alert-dismissible fade show mt-2 mb-0'>
                                        <Text
                                            id='lotTesting.aiExtract.success'
                                            fields={{
                                                lotCount: this.state.extractResult.lotCount,
                                                testCount: this.state.extractResult.testCount,
                                            }}
                                        >
                                            Filled {this.state.extractResult.lotCount} LOT fields, added {this.state.extractResult.testCount} weapon tests.
                                        </Text>
                                        <button type='button' class='btn-close' onClick={this.dismissExtractResult} />
                                    </div>
                                )}
                                {this.state.extractResult && this.state.extractResult.error && (
                                    <div class='alert alert-warning alert-dismissible fade show mt-2 mb-0'>
                                        <Text id='lotTesting.aiExtract.error'>Extraction failed. Please try again.</Text>
                                        <button type='button' class='btn-close' onClick={this.dismissExtractResult} />
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Rating Section */}
                        <div class='px-3 mt-4'>
                            <div class='card'>
                                <div class='card-body'>
                                    <h6 class='card-title mb-3'>
                                        <i class='fa-solid fa-star me-2' />
                                        Rating
                                    </h6>
                                    <div class='d-flex align-items-center'>
                                        {[1, 2, 3, 4, 5].map(star => (
                                            <button
                                                key={star}
                                                type='button'
                                                class='btn btn-link p-0 me-1'
                                                onClick={() => {
                                                    if (isNew) {
                                                        lotTestingStore.updateObjectKeyValue('newLotTesting', 'rating', star);
                                                    } else {
                                                        lotTestingStore.updateObjectKeyValue('lotTesting', 'rating', star);
                                                    }
                                                }}
                                                style='font-size: 1.5rem;'
                                            >
                                                <i class={`fa-${star <= (lotTesting.rating || 0) ? 'solid' : 'regular'} fa-star text-warning`} />
                                            </button>
                                        ))}
                                        {lotTesting.rating && (
                                            <span class='ms-2 text-muted'>({lotTesting.rating}/5)</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Weapon Tests Section */}
                        <div class='px-3 mt-4'>
                            <div class='d-flex justify-content-between align-items-center mb-3'>
                                <h5 class='mb-0'>
                                    <i class='fa-solid fa-gun me-2' />
                                    Weapon Tests ({weaponTests.length})
                                </h5>
                                {canEdit && (
                                    <button
                                        type='button'
                                        class='btn btn-sm btn-outline-primary rounded-pill'
                                        onClick={this.addWeaponTest}
                                    >
                                        <i class='fa-solid fa-plus me-1' />
                                        Add Test
                                    </button>
                                )}
                            </div>

                            {/* Weapon Tests List */}
                            {weaponTests.length === 0 ? (
                                <div class='text-center py-4 text-muted'>
                                    <i class='fa-solid fa-gun fa-2x mb-2' />
                                    <p class='mb-0'>No weapon tests yet.</p>
                                    {canEdit && (
                                        <button
                                            type='button'
                                            class='btn btn-sm btn-outline-primary rounded-pill mt-2'
                                            onClick={this.addWeaponTest}
                                        >
                                            <i class='fa-solid fa-plus me-1' />
                                            Add your first test
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div class='list-group'>
                                    {weaponTests.map(test => (
                                        <div key={test.uuidv4} class='list-group-item'>
                                            <div class='d-flex justify-content-between align-items-start'>
                                                <div class='flex-grow-1'>
                                                    <div class='d-flex justify-content-between'>
                                                        <h6 class='mb-1'>
                                                            {test.weaponBrand} {test.weaponModel}
                                                            {test.barrelLength ? ` (${test.barrelLength}")` : ''}
                                                        </h6>
                                                        <small class='text-muted'>
                                                            {test.testDate ? localUtil.formatDate(test.testDate) : '-'}
                                                        </small>
                                                    </div>
                                                    <div class='row g-2 mt-1'>
                                                        {test.shotCount && (
                                                            <div class='col-auto'>
                                                                <span class='badge bg-secondary'>
                                                                    <i class='fa-solid fa-hashtag me-1' />
                                                                    {test.shotCount} shots
                                                                </span>
                                                            </div>
                                                        )}
                                                        {test.isIndoor && (
                                                            <div class='col-auto'>
                                                                <span class='badge bg-dark'>
                                                                    <i class='fa-solid fa-warehouse me-1' />
                                                                    Indoor
                                                                </span>
                                                            </div>
                                                        )}
                                                        {(test.speed || test.speedMin || test.speedMax) && (
                                                            <div class='col-auto d-flex flex-wrap gap-1'>
                                                                {test.speed && (
                                                                    <span class='badge bg-primary'>
                                                                        <i class='fa-solid fa-gauge-high me-1' />
                                                                        {test.speed} {test.speedUnit || 'fps'}
                                                                    </span>
                                                                )}
                                                                {test.speedMin && test.speedMax && (
                                                                    <>
                                                                        <span class='badge bg-secondary'>
                                                                            {test.speedMin}-{test.speedMax}
                                                                        </span>
                                                                        <span class='badge bg-warning text-dark'>
                                                                            ES: {test.speedMax - test.speedMin}
                                                                        </span>
                                                                    </>
                                                                )}
                                                                {test.speedStdDev && (
                                                                    <span class='badge bg-info text-dark'>
                                                                        SD: {test.speedStdDev}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}
                                                        {test.accuracy && (
                                                            <div class='col-auto'>
                                                                <span class='badge bg-success'>
                                                                    <i class='fa-solid fa-bullseye me-1' />
                                                                    {test.accuracy} {test.accuracyUnit || 'MOA'}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {test.distance && (
                                                            <div class='col-auto'>
                                                                <span class='badge bg-info'>
                                                                    <i class='fa-solid fa-ruler me-1' />
                                                                    {test.distance} {test.distanceUnit || 'm'}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    {test.weather && (
                                                        <div class='mt-2 small text-muted'>
                                                            <i class='fa-solid fa-cloud me-1' />
                                                            {test.weather.temperature !== undefined && `${test.weather.temperature}C`}
                                                            {test.weather.humidity !== undefined && `, ${test.weather.humidity}%`}
                                                            {test.weather.windSpeed !== undefined && `, Wind: ${test.weather.windSpeed}m/s`}
                                                            {test.weather.pressure !== undefined && `, ${test.weather.pressure}hPa`}
                                                        </div>
                                                    )}
                                                    {test.notes && (
                                                        <div class='mt-2 small'>
                                                            <i class='fa-solid fa-note-sticky me-1' />
                                                            {test.notes}
                                                        </div>
                                                    )}
                                                </div>
                                                {canEdit && (
                                                    <div class='btn-group btn-group-sm ms-2'>
                                                        <button
                                                            type='button'
                                                            class='btn btn-outline-primary'
                                                            onClick={() => this.editWeaponTest(test)}
                                                            title='Edit test'
                                                        >
                                                            <i class='fa-solid fa-pen' />
                                                        </button>
                                                        <button
                                                            type='button'
                                                            class='btn btn-outline-danger'
                                                            onClick={(e) => this.removeWeaponTest(e, test.uuidv4)}
                                                            title='Remove test'
                                                        >
                                                            <i class='fa-solid fa-trash' />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                    </div>
                </div>

                {/* Action buttons - only show for new entries or when user can edit */}
                {canEdit && (
                    <div class='w-100 d-flex flex-column justify-content-start mt-3 px-4 py-3 bg-light fixed-bottom' style='bottom: 0; left: 0;'>
                        <div class='d-flex justify-content-end'>
                            <button
                                class='btn btn-primary rounded-pill'
                                onClick={this.saveLotTesting}
                            >
                                <i class='fa-solid fa-save me-1' />
                                {isNew ? 'Create LOT Entry' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

export default LotTestingView;
